"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

type ModalSheetSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";

const maxWidthClass: Record<ModalSheetSize, string> = {
  sm: "md:max-w-[452px]",
  md: "md:max-w-xl",
  lg: "md:max-w-2xl",
  xl: "md:max-w-3xl",
  "2xl": "md:max-w-4xl",
  "3xl": "md:max-w-[1120px]",
};

/** Duration for the close (exit) animation -- fast and snappy. */
const CLOSE_DURATION_MS = 250;

// Nested sheets share keyboard ownership and restore scrolling after the last closes.
const openSheets: HTMLDivElement[] = [];
const scrollLocks = new Map<HTMLElement, { count: number; overflow: string }>();

function isAvailableFocusTarget(element: HTMLElement) {
  if (element.matches(":disabled") || element.closest("[hidden], [inert]")) return false;
  if (getComputedStyle(element).visibility !== "visible") return false;
  if (element.checkVisibility) return element.checkVisibility();
  // Older browsers and DOM test environments have no checkVisibility.
  for (let parent: HTMLElement | null = element; parent; parent = parent.parentElement) {
    const style = getComputedStyle(parent);
    if (style.display === "none" || style.contentVisibility === "hidden") return false;
  }
  return true;
}

function focusableElements(sheet: HTMLElement) {
  return [...sheet.querySelectorAll<HTMLElement>(
    'a[href], button, textarea, input, select, [tabindex]',
  )].filter((element) => element.tabIndex >= 0 && isAvailableFocusTarget(element));
}

function lockScroll(element: HTMLElement) {
  const lock = scrollLocks.get(element) ?? { count: 0, overflow: element.style.overflow };
  lock.count += 1;
  scrollLocks.set(element, lock);
  element.style.overflow = "hidden";
  return () => {
    lock.count -= 1;
    if (lock.count === 0) {
      element.style.overflow = lock.overflow;
      scrollLocks.delete(element);
    }
  };
}

type Props = {
  isOpen: boolean;
  size?: ModalSheetSize;
  /**
   * Called when the user dismisses the sheet (backdrop click, close button, Escape).
   * The parent is responsible for setting isOpen=false in response.
   */
  onClose?: () => void;
  /**
   * When true, raises the backdrop + sheet above the default modal layer so a
   * nested sheet (e.g. an actions menu opened on top of another modal) dims and
   * sits above the modal beneath it. Use only for stacked-on-top sheets.
   */
  elevated?: boolean;
  /**
   * Panel background. "content" (default) uses the app gray so a body card can
   * float on it (the form-modal look); "card" uses the plain surface for
   * compact white dialogs (confirm/alert).
   */
  surface?: "content" | "card";
  /**
   * Presentation:
   *  - "sheet" (default): bottom sheet on mobile, centered
   *    modal on desktop.
   *  - "dialog": always centered with a scale/fade entrance on every
   *    breakpoint — no bottom-sheet slide, drag handle, or swipe. Use for
   *    compact confirm/alert dialogs.
   *  - "drawer": bottom sheet on mobile (same as "sheet"), but on desktop a
   *    full-height panel anchored to the end edge that slides in from the side.
   */
  variant?: "sheet" | "dialog" | "drawer";
  /**
   * Test hook, forwarded verbatim to the `role="dialog"` panel.
   *
   * Declared because a `data-testid` passed to a component whose props are a
   * closed object type is DROPPED — nothing spreads it onto an element — so a
   * caller that passed one got an attribute that never rendered, and any e2e
   * keyed on it matched nothing and passed vacuously. One optional attribute
   * on an element this already renders; no new API surface.
   */
  "data-testid"?: string;
  /**
   * Id of the element that names this dialog — rendered verbatim as
   * `aria-labelledby` on the `role="dialog"` panel.
   *
   * Declared for the same reason `data-testid` is: this component's props are a
   * closed object type and nothing is spread onto the panel, so there was no
   * path by which a caller COULD name its dialog. Without it every dialog in
   * the platform announces itself as an unnamed "dialog".
   *
   * Callers pass the id of a node they already render (`Modal` points at its
   * own title). Pointing at the node rather than hand-writing a string means
   * the name can never drift from the visible title, and CSS truncation does
   * not enter the accessible-name computation — a clipped title still yields
   * the complete name.
   */
  labelledBy?: string;
  /**
   * Literal accessible name, for the case where there is no title node to
   * point `labelledBy` at (e.g. a header replaced wholesale by a logo).
   * `labelledBy` wins when both are supplied.
   */
  ariaLabel?: string;
  children: React.ReactNode;
};

/**
 * Responsive modal wrapper.
 *
 * Mobile  -> slides up from bottom as a sheet (not draggable).
 * Desktop -> centered modal with subtle scale + fade entrance.
 *
 * iOS safe area is handled automatically via a spacer at the bottom.
 * No child needs to add env(safe-area-inset-bottom) manually.
 */
export function ModalSheet({ isOpen, size = "md", onClose, elevated = false, surface = "content", variant = "sheet", "data-testid": testId, labelledBy, ariaLabel, children }: Props) {
  const isDialog = variant === "dialog";
  const isDrawer = variant === "drawer";
  // Desktop drawer slide direction. `md:justify-end` anchors the panel at the
  // inline-end (right in LTR, left in RTL); the off-screen transform must match,
  // so compute it from `dir` rather than hardcoding translate-x-full (which slid
  // the wrong way in Arabic).
  const drawerClosedX =
    typeof document !== "undefined" && document.documentElement.dir === "rtl"
      ? "md:-translate-x-full"
      : "md:translate-x-full";

  // Freeze the rendered children through the close animation. Parents commonly
  // clear the backing state (e.g. `setSelected(null)`) in the same tick they
  // close the modal, which would otherwise flash the empty/default content for
  // the ~250ms exit. While open we track the latest children; while closing we
  // keep showing the last ones.
  const [content, setContent] = useState({ open: isOpen, children });
  // isMounted stays true during the close animation so the element is in DOM.
  const [isMounted, setIsMounted] = useState(isOpen);
  // isVisible drives the CSS animation class (translate-y-0 vs translate-y-full).
  const [isVisible, setIsVisible] = useState(false);
  if (content.open !== isOpen || (isOpen && content.children !== children)) {
    setContent({ open: isOpen, children: isOpen ? children : content.children });
    if (content.open !== isOpen) {
      setIsVisible(false);
    }
  }
  // A persistent sheet may finish its previous closed lifetime before opening.
  // Mount from the current open value, independently of the content snapshot.
  if (isOpen && !isMounted) setIsMounted(true);

  const sheetRef = useRef<HTMLDivElement>(null);
  const isTopSheet = () => openSheets.at(-1) === sheetRef.current;
  const backdropRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);

  // Stable ref to onClose so event listeners never go stale.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // React to isOpen prop changes.
  useEffect(() => {
    if (isOpen) {
      // Double-RAF ensures a real paint frame between mount and the class
      // change so the CSS transition always plays reliably.
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      closeTimerRef.current = setTimeout(() => {
        setIsMounted(false);
      }, CLOSE_DURATION_MS);
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOpen]);

  // Focus trap -- lock focus within the modal while open.
  const previouslyFocusedRef = useRef<Element | null>(null);

  useEffect(() => {
    if (!isOpen || !sheetRef.current) return;
    const sheet = sheetRef.current;
    openSheets.push(sheet);
    return () => { const index = openSheets.indexOf(sheet); if (index >= 0) openSheets.splice(index, 1); };
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isOpen || !isMounted || !sheetRef.current) return;
    previouslyFocusedRef.current = document.activeElement;

    // Wait for mount before querying focusable elements.
    const raf = requestAnimationFrame(() => {
      const sheet = sheetRef.current;
      if (!sheet || !isTopSheet()) return;
      const focusable = focusableElements(sheet);

      /*
       * Land on the first FIELD, not the first focusable thing.
       *
       * In DOM order the first focusable element of a sheet is its close X, so
       * opening any form modal used to put the keyboard on "close" — a
       * keyboard user's first Enter dismissed the dialog they had just opened,
       * and a sighted user's first keystroke went nowhere.
       *
       * A dialog with NO field keeps the old behaviour deliberately, and that
       * is the important half: a ConfirmModal's focusables are its Cancel and
       * its Confirm, and auto-focusing a destructive Confirm would make Enter
       * delete something. No field means nothing to type into, so the close
       * control is the safe place to be.
       *
       * `[data-autofocus]` overrides both, for the dialog whose useful first
       * field is not its first one.
       */
      const explicit = [...sheet.querySelectorAll<HTMLElement>("[data-autofocus]")]
        .find(isAvailableFocusTarget);
      if (explicit) {
        explicit.focus();
        return;
      }

      /*
       * A TOUCH screen gets no field focus at all (founder, 2026-10-06): on a
       * phone a focused field pops the keyboard over the sheet, and a focused
       * picker opens its list — the void dialog opened with its reasons
       * already dropped down over its own title. The sheet itself takes focus
       * (Escape and the focus trap still work); the person taps what they mean.
       */
      const touch =
        typeof window !== "undefined" &&
        window.matchMedia?.("(pointer: coarse)").matches === true;
      if (touch) {
        sheet.focus();
        return;
      }

      const field = [...focusable].find((element) => {
        // A picker opens its list on focus (Headless UI's `immediate`), so it
        // is never the field a sheet opens on: the list would cover the sheet
        // before anyone asked for it.
        if (element.getAttribute("role") === "combobox") return false;
        const tag = element.tagName;
        if (tag === "TEXTAREA" || tag === "SELECT") return true;
        if (tag !== "INPUT") return false;
        const input = element as HTMLInputElement;
        // A hidden or read-only input is not somewhere a person can type, and
        // a checkbox or radio is a control rather than a field to fill — the
        // first thing a form asks for is almost never a tickbox, and focusing
        // one makes space toggle it before the reader has read the label.
        if (input.readOnly || input.type === "hidden") return false;
        return input.type !== "checkbox" && input.type !== "radio";
      });

      (field ?? focusable[0] ?? sheet).focus();
    });

    return () => {
      cancelAnimationFrame(raf);
      // Restore focus to the element that was focused before the modal opened.
      const prev = previouslyFocusedRef.current;
      if (prev && prev instanceof HTMLElement) {
        prev.focus();
      }
    };
  }, [isOpen, isMounted]);

  useEffect(() => {
    if (!isOpen) return;
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !isTopSheet()) return;
      const sheet = sheetRef.current;
      if (!sheet) return;
      const focusable = focusableElements(sheet);
      if (focusable.length === 0) {
        e.preventDefault();
        sheet.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (active instanceof HTMLElement && active !== sheet && !focusable.includes(active)) {
        // Explicit autofocus may have tabIndex=-1. Follow DOM order, wrapping
        // when it lies beyond the sequential controls, instead of escaping.
        const candidates = e.shiftKey ? [...focusable].reverse() : focusable;
        const direction = e.shiftKey ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING;
        const next = sheet.contains(active) ? candidates.find(element => Boolean(active.compareDocumentPosition(element) & direction)) : undefined;
        e.preventDefault();
        (next ?? (e.shiftKey ? last : first)).focus();
        return;
      }
      if (e.shiftKey) {
        if (document.activeElement === first || document.activeElement === sheet) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || document.activeElement === sheet) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleTabKey);
    return () => document.removeEventListener("keydown", handleTabKey);
  }, [isOpen]);

  // Body scroll lock -- prevent background scrolling while the sheet is open.
  useEffect(() => {
    if (!isOpen) return;
    // Lock the REAL scroller, not just <body>. Shells that scroll inside an
    // inner element (the restaurant app's `.app-main`) mark it with
    // `data-app-scroll`; locking body alone left the page scrollbar live
    // behind the sheet — the contract's "scrollable backdrop" broken pattern.
    const scroller = document.querySelector<HTMLElement>("[data-app-scroll]");
    const unlockScroller = scroller ? lockScroll(scroller) : undefined;
    const unlockBody = lockScroll(document.body);
    return () => {
      unlockScroller?.();
      unlockBody();
    };
  }, [isOpen]);

  // Escape key.
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isTopSheet()) {
        e.preventDefault();
        onCloseRef.current?.();
      }
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen]);

  // No drag (founder, 2026-10-06): the sheet used to follow any finger drag
  // — scrolling its own content included — and close past 120px, so it could
  // be shoved around the screen, at every width. It is dismissed by a tap on
  // the backdrop, its close button or Escape; it never moves under a finger.

  if (!isMounted) return null;
  if (typeof document === "undefined") return null;

  // Portal to <body> so the sheet's `position: fixed` anchors to the viewport
  // even when an ancestor is transformed (e.g. a page-transition container),
  // which would otherwise become the containing block and mis-place the modal.
  return createPortal(
    <>
      {/* Backdrop. Elevated sheets stack on a base backdrop, so they tint only
          lightly to avoid compounding into near-black; the full-bleed (order)
          sheet sits over a dark page so it also tints softer than the default. */}
      <div
        ref={backdropRef}
        data-ui="dialog-backdrop"
        className={[
          "fixed inset-0 transition-opacity",
          // Elevated (stacked) sheets sit on a base backdrop, so they tint only
          // lightly to avoid compounding into near-black.
          elevated ? "z-[60] bg-black/10" : "z-40 bg-black/40",
          // Backdrop mirrors the sheet: slow fade-in, fast fade-out
          isVisible ? "opacity-100 duration-[450ms]" : "opacity-0 duration-[250ms]",
        ].join(" ")}
        aria-hidden="true"
        onClick={() => onCloseRef.current?.()}
      />

      {/* Sheet positioner -- pointer-events-none so clicks pass through to backdrop */}
      <div className={[
        "pointer-events-none fixed inset-0 flex",
        // Dialog: centered with padding on every breakpoint.
        // Drawer: bottom sheet on mobile, end-anchored full-height on desktop.
        // Sheet: bottom-anchored on mobile, centered on desktop.
        isDialog
          ? "items-center justify-center p-6"
          : isDrawer
            ? "items-end md:items-stretch md:justify-end"
            : "items-end md:items-center md:justify-center md:p-6",
        elevated ? "z-[70]" : "z-50",
      ].join(" ")}>
        <div
          ref={sheetRef}
          role="dialog"
          tabIndex={-1}
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-label={labelledBy ? undefined : ariaLabel}
          data-testid={testId}
          data-ui="dialog-panel"
          data-variant={variant}
          data-size={size}
          className={[
            // `outline-none`: the panel takes focus itself on a touch screen
            // (see the focus effect above). It is not a control, so it must
            // not paint the browser's focus ring — an auto-opened sheet (the
            // install sheet) drew one around its whole edge.
            "pointer-events-auto flex w-full flex-col outline-none",
            // Panel sits on the app "content" gray so the body card floats as a
            // white inset (new modal look). Falls back to the surface colour in
            // apps that don't define --content. "card" surface keeps the plain
            // white panel for compact confirm/alert dialogs.
            "border-[length:var(--border-card-width,1px)] border-[color:var(--border-light)] shadow-[var(--shadow-lg)]",
            surface === "card"
              ? "bg-[color:var(--surface)]"
              : "bg-[color:var(--content,var(--surface))]",
            // Transition applies to transform + opacity + scale.
            // The duration/easing in each state applies while LEAVING that state,
            // so open-curve lives in the closed class and close-curve in the open class.
            "transition-all will-change-transform",
            isDialog
              ? // Centered scale/fade on all breakpoints (classic alert dialog).
                `max-h-[90vh] rounded-[var(--radius-sheet,18px)] ${maxWidthClass[size]} ${
                  isVisible
                    ? "opacity-100 scale-100 duration-[250ms] ease-in"
                    : "opacity-0 scale-[0.97] duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                }`
              : isDrawer
                ? // Bottom sheet (mobile) -> end-anchored full-height panel (desktop).
                  [
                    "max-h-[92vh] rounded-t-[20px]",
                    `md:h-[100dvh] md:max-h-[100dvh] md:rounded-none md:rounded-s-[var(--radius-sheet,18px)] md:border-s ${maxWidthClass[size]}`,
                    isVisible
                      ? "translate-y-0 opacity-100 md:translate-x-0 md:opacity-100 duration-[250ms] ease-in"
                      : `translate-y-full opacity-100 md:translate-y-0 ${drawerClosedX} md:opacity-100 duration-[250ms] ease-[cubic-bezier(0.4,0,1,1)]`,
                  ].join(" ")
                : // Bottom sheet (mobile) -> centered modal (desktop).
                  [
                    "max-h-[92vh] rounded-t-[20px]",
                    `md:rounded-[var(--radius-sheet,18px)] md:max-h-[90vh] ${maxWidthClass[size]}`,
                    isVisible
                      ? "translate-y-0 opacity-100 md:scale-100 duration-[250ms] ease-in"
                      : "translate-y-full md:translate-y-0 md:scale-[0.97] md:opacity-0 duration-[450ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
                  ].join(" "),
          ].join(" ")}
        >
          {isOpen ? children : content.children}

          {/*
           * Safe area spacer -- equals the iOS home-indicator inset for the
           * bottom sheet. Centered dialogs don't touch the home indicator.
           */}
          {isDialog ? null : (
            <div aria-hidden="true" className="h-[env(safe-area-inset-bottom)] shrink-0" />
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}
